<template>
  <div class="json-tree-d3">
    <svg ref="svgRef"></svg>
  </div>
</template>

<script lang="ts">
import * as d3 from 'd3';
import { defineComponent, onMounted, ref, watch } from 'vue';

// Define a type for the D3 tree node structure
interface D3TreeNode {
  name: string | number;
  value?: any; // Value can be of any type or optional
  children?: D3TreeNode[];
  _children?: D3TreeNode[]; // For collapsed nodes
  // D3 hierarchy properties. These are added by d3.hierarchy
  parent?: any | null;
  depth?: number;
  height?: number;
  // Below are d3.HierarchyPointNode properties after layout is computed
  x?: number;
  y?: number;
  // Original data reference, D3 adds this automatically
  data: any; // CHANGED: Was D3TreeNode, breaking the circular dependency
  // For transitions
  x0?: number;
  y0?: number;
  id?: string | number; // Ensure id is typed, d3 might assign numbers or rely on data ids
}

function toD3Tree(inputData: unknown, key: string | number = 'root'): D3TreeNode {
  if (typeof inputData !== 'object' || inputData === null || (Array.isArray(inputData) && typeof inputData[0] === 'function')) {
    // For a leaf node, don't truncate values - let the text wrapping handle it
    return { name: key, value: inputData, data: { name: key, value: inputData } } as D3TreeNode;
  }
  const children: D3TreeNode[] = Array.isArray(inputData)
    ? inputData.map((item, i) => toD3Tree(item, i))
    : Object.entries(inputData).map(([k, v]) => toD3Tree(v, k));
  return { name: key, children, data: { name: key, children: inputData } } as D3TreeNode;
}

// Helper function for text wrapping - completely rewritten for better handling of long text
function wrapText(selection: any, maxWidth: number) {
  selection.each(function(this: SVGTextElement) {
    const textD3 = d3.select(this);
    const originalText = textD3.text();
    textD3.text(null); // Clear original text

    // Split by natural breaks first (newlines)
    const paragraphs = originalText.split(/\n/);
    
    let dy = 0;
    const lineHeight = 1.2; // in ems
    const initialDy = parseFloat(textD3.attr('dy') || '0');
    
    // Process each paragraph
    paragraphs.forEach((paragraph: string, paragraphIndex: number) => {
      // Additional line break between paragraphs after the first one
      if (paragraphIndex > 0) {
        dy += 0.6; // Add extra space between paragraphs
      }
      
      // Split paragraph into words
      const words = paragraph.split(/\s+/);
      let line: string[] = [];
      let tspan = textD3.append('tspan')
          .attr('x', 0)
          .attr('dy', paragraphIndex === 0 ? initialDy + 'em' : lineHeight + 'em');
      
      // Build each line
      words.forEach((word: string) => {
        line.push(word);
        tspan.text(line.join(' '));
        
        // If we exceed width and have more than one word, wrap
        if (tspan.node().getComputedTextLength() > maxWidth && line.length > 1) {
          line.pop(); // Remove word that caused overflow
          tspan.text(line.join(' '));
          
          // Start a new line with the overflow word
          line = [word];
          tspan = textD3.append('tspan')
              .attr('x', 0)
              .attr('dy', lineHeight + 'em')
              .text(word);
        }
      });
    });
  });
}

export default defineComponent({
  name: 'JSONTreeView',
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const svgRef = ref<SVGSVGElement | null>(null);
    let root: any | null = null;
    let treeLayout: any | null = null;
    let svg: any | null = null;
    let g: any | null = null;
    let duration = 300;
    let currentWidth = ref(700);
    let dx = 60;
    let dy = 220;

    const renderTree = (treeDataInput: any) => {
      if (!svgRef.value) return;
      d3.select(svgRef.value).selectAll('*').remove();

      const hierarchyData = toD3Tree(treeDataInput);
      root = d3.hierarchy(hierarchyData) as any;
      root.x0 = 0;
      root.y0 = 0;

      // Calculate a better width based on data complexity
      const estimatedWidth = Math.max(1200, Object.keys(treeDataInput).length * 250);
      currentWidth.value = estimatedWidth;

      svg = d3.select(svgRef.value)
        .attr('width', currentWidth.value)
        .attr('height', 900)
        .attr('viewBox', `0 0 ${currentWidth.value} 950`)
        .style('background', '#fff');
      
      // Add more left margin for better visualization
      g = svg.append('g').attr('transform', 'translate(80,150)');
      
      // Increase spacing significantly for better text display
      treeLayout = d3.tree().nodeSize([dx * 3, dy * 1.6]);
      root.children?.forEach(collapse);
      update(root);
    };

    function collapse(d: any) {
      if (d.children) {
        d._children = d.children;
        d._children.forEach(collapse);
        d.children = undefined;
      }
    }

    function update(source: any) {
      const newTreeLayout = treeLayout!(root!);
      const allNodes = newTreeLayout.descendants() as any[];
      const links = newTreeLayout.links() as any[];

      let minY_vertical = 0;
      let maxY_vertical = 0;
      if (allNodes.length > 0) {
        minY_vertical = d3.min(allNodes, (dNode: any) => dNode.x!) as number;
        maxY_vertical = d3.max(allNodes, (dNode: any) => dNode.x!) as number;
      }

      const verticalOffsetG = 120;
      const verticalPadding = dx * 2;
      const viewBoxMinY_svgCoords = (verticalOffsetG + minY_vertical) - verticalPadding / 2;
      const contentSpanHeight = maxY_vertical - minY_vertical;
      const viewBoxHeight_svgCoords = contentSpanHeight + verticalPadding;

      let maxX_horizontal_layout = 0;
      if (allNodes.length > 0) {
        maxX_horizontal_layout = d3.max(allNodes, (dNode: any) => dNode.y!) as number;
      }
      const horizontalOffsetG = 40;
      const textPadding = 400; // Increased from 300 for wider text blocks
      const estimatedContentWidth = maxX_horizontal_layout + textPadding + horizontalOffsetG;
      currentWidth.value = Math.max(1000, estimatedContentWidth);
      
      const viewBoxMinX_svgCoords = 0;

      svg.transition().duration(duration)
         .attr('width', currentWidth.value)
         .attr('height', viewBoxHeight_svgCoords)
         .attr('viewBox', `${viewBoxMinX_svgCoords} ${viewBoxMinY_svgCoords} ${currentWidth.value} ${viewBoxHeight_svgCoords}`);

      // Nodes
      const node = g!.selectAll('g.node')
        .data(allNodes, (dNode: any) => dNode.id || (dNode.id = Math.random().toString()));

      const nodeEnter = node.enter().append('g')
        .attr('class', 'node')
        .attr('transform', `translate(${source.y0},${source.x0})`)
        .on('click', (event: MouseEvent, dNode: any) => {
          if (dNode.children) {
            dNode._children = dNode.children;
            dNode.children = undefined;
          } else {
            dNode.children = dNode._children;
            dNode._children = undefined;
          }
          update(dNode);
        });

      nodeEnter.append('circle')
        .attr('r', 16)
        .attr('fill', (dNode: any) => dNode._children ? '#2196F3' : '#fff')
        .attr('stroke', '#2196F3')
        .attr('stroke-width', 2);

      nodeEnter.append('text')
        .attr('class', 'circle-text')
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'central')
        .text((dNode: any) => {
          const name = dNode.data.name;
          if (typeof name === 'number' || (typeof name === 'string' && name.length <= 2)) {
            return name;
          }
          return '';
        })
        .style('font-size', '12px')
        .style('font-family', 'Arial, sans-serif')
        .style('font-weight', 'bold')
        .style('fill', '#333')
        .style('pointer-events', 'none');

      // Add the node label text with special handling for certain field types
      nodeEnter.append('text')
        .attr('class', 'node-label')
        .attr('dy', '0em')
        .attr('x', (dNode: any) => {
          // Increase space between node and label based on type
          const isParent = dNode.children || dNode._children;
          
          // Give more space for specific node types that typically have longer text
          const name = String(dNode.data.name).toLowerCase();
          const needsExtraSpace = name.includes('option') || 
                                 name.includes('group') || 
                                 name === 'ids';
          
          const needsMoreSpace = name.includes('description') || 
                                 name.includes('items');
          
          // Adjust x position based on node type
          if (isParent) {
            return needsExtraSpace ? -60 : -50;
          } else {
            return needsExtraSpace ? 60 : (needsMoreSpace ? 55 : 45);
          }
        })
        .attr('text-anchor', (dNode: any) => (dNode.children || dNode._children) ? 'end' : 'start')
        .text((dNode: any) => {
          const name = dNode.data.name;
          // Skip short names as they're shown in the circle
          if (typeof name === 'number' || (typeof name === 'string' && name.length <= 2)) {
            if (dNode.data.value !== undefined) {
              return formatNodeValue(dNode.data.value);
            }
            return '';
          }
          
          if (dNode.data.value !== undefined) {
            // Handle array/object values better
            if (typeof dNode.data.value === 'object' && dNode.data.value !== null) {
              if (Array.isArray(dNode.data.value)) {
                // For arrays like optionGroupIds, just show length with [...] notation
                if (name.toLowerCase().includes('ids') || name.toLowerCase().includes('group')) {
                  return `${dNode.data.name}: [${dNode.data.value.length} items]`;
                }
              }
            }
            return `${dNode.data.name}: ${formatNodeValue(dNode.data.value)}`;
          }
          return dNode.data.name;
        })
        .style('font-size', '14px')
        .style('line-height', '1.5')
        .style('font-family', 'monospace')
        .style('fill', '#333')
        .style('pointer-events', 'none')
        .call(wrapText, dy - 100);

      // Transition nodes to their new position
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const nodeUpdate = nodeEnter.merge(node)
        .transition().duration(duration)
        .attr('transform', (dNode: any) => `translate(${dNode.y!},${dNode.x!})`);

      // Remove exiting nodes
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const nodeExit = node.exit().transition().duration(duration)
        .attr('transform', `translate(${source.y},${source.x})`)
        .remove();

      // Links
      const link = g!.selectAll('path.link')
        .data(links, (dLink: any) => (dLink.target as any).id! );

      link.enter().insert('path', 'g')
        .attr('class', 'link')
        .attr('d', (dLink: any) => {
          const o = { x: source.x0!, y: source.y0! };
          return diagonal({ source: o, target: o });
        })
        .attr('fill', 'none')
        .attr('stroke', '#cccccc')
        .attr('stroke-width', 1.5)
        .attr('stroke-opacity', 0.7)
        .merge(link)
        .transition().duration(duration)
        .attr('d', diagonal);

      link.exit().transition().duration(duration)
        .attr('d', (dLink: any) => {
          const o = { x: source.x!, y: source.y! }; // Use non-null assertion
          return diagonal({ source: o, target: o });
        })
        .remove();

      // Stash the old positions for transition.
      allNodes.forEach((dNode: any) => {
        dNode.x0 = dNode.x!;
        dNode.y0 = dNode.y!;
      });
    }

    interface DiagonalPoint {
      x: number;
      y: number;
    }

    interface DiagonalLink {
      source: DiagonalPoint;
      target: DiagonalPoint;
    }

    function diagonal(d: DiagonalLink | any): string {
      const sourceX = 'source' in d ? d.source.x : (d as { source: DiagonalPoint }).source.x;
      const sourceY = 'source' in d ? d.source.y : (d as { source: DiagonalPoint }).source.y;
      const targetX = 'target' in d ? d.target.x : (d as { target: DiagonalPoint }).target.x;
      const targetY = 'target' in d ? d.target.y : (d as { target: DiagonalPoint }).target.y;
      return `M${sourceY},${sourceX}` +
             `C${(sourceY + targetY) / 2},${sourceX} ` +
             `${(sourceY + targetY) / 2},${targetX} ` +
             `${targetY},${targetX}`;
    }

    // Helper function to properly format node values
    function formatNodeValue(value: any): string {
      if (value === null) return 'null';
      if (value === undefined) return 'undefined';
      
      if (typeof value === 'string') {
        // Don't truncate strings anymore - wrapping will handle it
        return `"${value}"`;
      }
      
      if (typeof value === 'object') {
        // Handle arrays better
        if (Array.isArray(value)) {
          if (value.length <= 3) {
            // Show small arrays in full
            return JSON.stringify(value);
          } else {
            // For larger arrays, show just the length
            return `[${value.length} items]`;
          }
        }
        // For objects, show a simplified representation
        const keys = Object.keys(value || {});
        if (keys.length <= 2) {
          return JSON.stringify(value);
        } else {
          return `{${keys.length} props}`;
        }
      }
      
      return String(value);
    }

    onMounted(() => {
      renderTree(props.data);
    });

    watch(() => props.data, (newVal) => {
      renderTree(newVal);
    }, { deep: true });

    return { svgRef };
  }
});
</script>

<style scoped>
.json-tree-d3 {
  width: 100%;
  height: 100%;
  overflow: auto;
  background: #f9f9f9;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  margin-bottom: 20px;
}

svg {
  width: 100%;
  min-width: 600px;
  display: block;
}

.node circle {
  cursor: pointer;
  transition: all 0.3s ease;
}

.node:hover circle {
  fill: #1976D2 !important;
  filter: drop-shadow(0 0 2px rgba(25, 118, 210, 0.4));
}

.node .circle-text {
  font-weight: bold;
  user-select: none;
}

.node .node-label {
  user-select: none;
}

.node:hover .circle-text {
  fill: white;
}

.link {
  fill: none;
  transition: stroke 0.2s;
}

/* For better appearance on high-DPI displays */
@media (min-resolution: 192dpi) {
  .link {
    shape-rendering: geometricPrecision;
  }
  
  .node circle {
    shape-rendering: geometricPrecision;
  }
}
</style> 